import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Send, Loader, Book, FileText, CornerDownLeft, Trash2, Bot, User as UserIcon, MessageSquare, Sparkles, ChevronDown, ChevronUp, History, Paperclip, Image, FileUp, Volume2, VolumeX, Settings, Lock, Crown, AlertCircle, Zap, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { FormattedMessage } from './FormattedMessage';
import { askContextualTutor, type ConversationTurn } from '@/services/contextualTutorService';
import { processUploadedFile, formatFileSize, type ProcessedFile } from '@/utils/fileUploadUtils';
import { useTextToSpeech } from '@/hooks/useTextToSpeech';
import { useTouchFeedback } from '@/hooks/useTouchFeedback';
import type { RoadmapData, ArticleData } from '@/types';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/config/supabase';
import { recordToArticleData } from '@/services/articleService';
import { canSendFreeTutorMessage, getRemainingFreeTutorMessages, incrementFreeMessages } from '@/services/trialService';

interface ContextualTutorModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  roadmaps: RoadmapData[];
  articles: Record<string, ArticleData>;
  isDark?: boolean;
  isPro?: boolean;
  onUpgradeClick?: () => void;
}

interface PersistedConversation {
  id: string; // articleId
  turns: (ConversationTurn & { timestamp: number })[];
}

const ContextualTutorModal: React.FC<ContextualTutorModalProps> = ({ isOpen, onClose, user, roadmaps, articles, isDark = true, isPro = false, onUpgradeClick }) => {
  const { triggerHaptic } = useTouchFeedback({ enableHaptics: true });
  const [conversation, setConversation] = useState<(ConversationTurn & { timestamp: number })[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedRoadmapId, setSelectedRoadmapId] = useState<string | null>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  
  const [currentArticle, setCurrentArticle] = useState<ArticleData | null>(null);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [fetchedArticles, setFetchedArticles] = useState<Record<string, ArticleData>>({});

  const [attachedFiles, setAttachedFiles] = useState<ProcessedFile[]>([]);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Trial/Free tier tracking
  const [freeMessagesRemaining, setFreeMessagesRemaining] = useState(3);
  const [canSendMessage, setCanSendMessage] = useState(true);

  // Text-to-speech
  const { speak: basespeak, stop, isPlaying, isSpeechSupported, availableVoices, getVoicesByGenderAndLanguage } = useTextToSpeech();
  const [playingMessageIndex, setPlayingMessageIndex] = useState<number | null>(null);
  
  // Settings
  const [showSettings, setShowSettings] = useState(false);
  const [responseLanguage, setResponseLanguage] = useState('en');
  const [voiceGender, setVoiceGender] = useState<'male' | 'female'>('female');
  const [selectedVoiceIndex, setSelectedVoiceIndex] = useState(0);
  const [textSize, setTextSize] = useState<'sm' | 'md' | 'lg'>('md');
  const [compactMode, setCompactMode] = useState(false);

  // Performance optimization - memoized values
  const filteredVoices = useMemo(() => 
    getVoicesByGenderAndLanguage(voiceGender, responseLanguage),
    [voiceGender, responseLanguage, getVoicesByGenderAndLanguage]
  );

  const conversationStats = useMemo(() => ({
    messageCount: conversation.length,
    firstMessageTime: conversation[0]?.timestamp,
    lastMessageTime: conversation[conversation.length - 1]?.timestamp,
  }), [conversation]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (chatBodyRef.current) {
      const scrollHeight = chatBodyRef.current.scrollHeight;
      chatBodyRef.current.scrollTop = scrollHeight;
    }
  }, [conversation, isLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [userInput]);

  // Check free message availability when modal opens or user logs in
  useEffect(() => {
    if (isOpen && user && !isPro) {
      const remaining = getRemainingFreeTutorMessages(user.id);
      setFreeMessagesRemaining(remaining);
      setCanSendMessage(remaining > 0);
    } else if (isPro) {
      setFreeMessagesRemaining(3);
      setCanSendMessage(true);
    }
  }, [isOpen, user?.id, isPro]);

  // Load settings from localStorage
  useEffect(() => {
    const savedGender = (localStorage.getItem('tutor_voice_gender') || 'female') as 'male' | 'female';
    const savedVoice = localStorage.getItem('tutor_voice_index') || '0';
    const savedTextSize = (localStorage.getItem('tutor_text_size') || 'md') as 'sm' | 'md' | 'lg';
    const savedCompactMode = localStorage.getItem('tutor_compact_mode') === 'true';
    setVoiceGender(savedGender);
    setSelectedVoiceIndex(parseInt(savedVoice));
    setTextSize(savedTextSize);
    setCompactMode(savedCompactMode);
  }, []);

  // Save settings to localStorage
  const updateSettings = useCallback((language: string, gender: 'male' | 'female', voiceIndex?: number, newTextSize?: 'sm' | 'md' | 'lg', newCompactMode?: boolean) => {
    setResponseLanguage(language);
    setVoiceGender(gender);
    const voiceToUse = voiceIndex ?? 0;
    setSelectedVoiceIndex(voiceToUse);
    localStorage.setItem('tutor_voice_gender', gender);
    localStorage.setItem('tutor_voice_index', voiceToUse.toString());
    if (newTextSize) {
      setTextSize(newTextSize);
      localStorage.setItem('tutor_text_size', newTextSize);
    }
    if (newCompactMode !== undefined) {
      setCompactMode(newCompactMode);
      localStorage.setItem('tutor_compact_mode', newCompactMode.toString());
    }
  }, []);

  // Load conversation from LocalStorage

  useEffect(() => {
    if (selectedArticleId) {
      const storageKey = `nanolez_chat_history_${selectedArticleId}`;
      const savedChat = localStorage.getItem(storageKey);
      if (savedChat) {
        try {
          const parsed = JSON.parse(savedChat) as PersistedConversation;
          setConversation(parsed.turns || []);
        } catch (e) {
          setConversation([]);
        }
      } else {
        setConversation([]);
      }
    }
  }, [selectedArticleId]);

  // Save conversation to LocalStorage
  useEffect(() => {
    if (selectedArticleId && conversation.length > 0) {
      const storageKey = `nanolez_chat_history_${selectedArticleId}`;
      localStorage.setItem(storageKey, JSON.stringify({ id: selectedArticleId, turns: conversation }));
    }
  }, [conversation, selectedArticleId]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({ top: chatBodyRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [conversation, isLoading]);

  // Stop audio when modal closes
  useEffect(() => {
    if (!isOpen) {
      stop();
      setPlayingMessageIndex(null);
    }
  }, [isOpen, stop]);

  useEffect(() => {
    if (selectedArticleId) {
      setCurrentArticle(fetchedArticles[selectedArticleId] || null);
    } else {
      setCurrentArticle(null);
    }
  }, [selectedArticleId, fetchedArticles]);

  // File handling functions
  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return;
    
    for (let i = 0; i < files.length; i++) {
      try {
        const processedFile = await processUploadedFile(files[i]);
        setAttachedFiles(prev => [...prev, processedFile]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to process file');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeAttachedFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSpeakMessage = (messageIndex: number, content: string) => {
    if (playingMessageIndex === messageIndex) {
      // Stop if already playing this message
      stop();
      setPlayingMessageIndex(null);
    } else {
      // Start playing this message
      // Extract plain text from formatted content (remove markdown)
      const plainText = content
        .replace(/\*\*(.+?)\*\*/g, '$1')  // Remove bold markers
        .replace(/\*(.+?)\*/g, '$1')      // Remove italic markers
        .replace(/`([^`]+)`/g, '$1')      // Remove inline code markers
        .replace(/```[\s\S]*?```/g, '')   // Remove code blocks
        .replace(/#{1,6}\s+/g, '')        // Remove heading markers
        .replace(/^[\s]*([-•])\s+/gm, ''); // Remove list markers
      
      // Use the actual voice index from filtered voices
      const voiceToUse = filteredVoices.length > selectedVoiceIndex ? filteredVoices[selectedVoiceIndex].idx : filteredVoices[0]?.idx ?? 0;
      basespeak(plainText, voiceToUse);
      setPlayingMessageIndex(messageIndex);
    }
  };

  if (!isOpen) return null;

  const handleSendMessage = async (textOverride?: string) => {
    const input = textOverride || userInput;
    if (!input.trim() || isLoading) return;
    if (!currentArticle) {
      setError("Please select a topic first.");
      return;
    }

    // Trigger haptic feedback
    triggerHaptic('light');

    // Check free message limit for non-Pro users
    if (!isPro && user) {
      if (!canSendFreeTutorMessage(user.id)) {
        setError("You've reached your 3 free messages for today. Upgrade to Pro for unlimited access.");
        return;
      }
      // Increment the counter
      const remaining = getRemainingFreeTutorMessages(user.id) - 1;
      incrementFreeMessages(user.id);
      setFreeMessagesRemaining(remaining);
    }

    setError(null);
    setIsLoading(true);

    const userMessage = { role: 'user' as const, content: input, timestamp: Date.now() };
    const newConversation = [...conversation, userMessage];
    setConversation(newConversation);
    if (!textOverride) setUserInput('');

    try {
      const answer = await askContextualTutor(
        input,
        currentArticle,
        newConversation.map(({ role, content }) => ({ role, content })).slice(0, -1),
        currentArticle?.language || 'en',
        attachedFiles.length > 0 ? attachedFiles : undefined
      );
      
      triggerHaptic('medium');
      setConversation(prev => [...prev, { role: 'assistant', content: answer, timestamp: Date.now() }]);
      setAttachedFiles([]); // Clear files after sending
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect to AI.');
      if (!textOverride) setUserInput(input);
      setConversation(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    if (selectedArticleId && window.confirm('Clear this chat?')) {
      localStorage.removeItem(`nanolez_chat_history_${selectedArticleId}`);
      setConversation([]);
    }
  };

  const handleRoadmapChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roadmapId = e.target.value;
    setSelectedRoadmapId(roadmapId);
    setSelectedArticleId(null);
    setFetchedArticles({});
    if (roadmapId) {
      setLoadingArticles(true);
      const { data } = await supabase.from('articles').select('*').eq('roadmap_id', roadmapId);
      if (data) {
        const mapped = data.reduce((acc, r) => ({ ...acc, [r.id]: recordToArticleData(r) }), {});
        setFetchedArticles(mapped);
      }
      setLoadingArticles(false);
    }
  };

  const suggestedQuestions = currentArticle ? [
    `Simplify this for a beginner`,
    `Give me a real-life analogy`,
    `What are the 3 key takeaways?`,
    ...(currentArticle.technicalConcepts?.slice(0, 1).map(c => `Tell me more about ${c.term}`) || [])
  ] : [];

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-0 md:p-6 backdrop-blur-md animate-in fade-in transition-all">
      <div className="bg-[#0f172a] text-white w-full max-w-5xl h-full md:h-[85vh] md:rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-white/10 relative">
        
        {/* Free Messages Limit Alert for Non-Pro Users */}
        {!isPro && user && freeMessagesRemaining > 0 && freeMessagesRemaining <= 1 && (
          <div className="bg-orange-500/20 border border-orange-500/50 px-4 py-3 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="text-orange-200 font-semibold">Limited Free Messages</p>
              <p className="text-orange-100/80">You have {freeMessagesRemaining} free message{freeMessagesRemaining !== 1 ? 's' : ''} left today</p>
            </div>
          </div>
        )}

        {/* Messages Exhausted Message */}
        {!isPro && user && freeMessagesRemaining === 0 && (
          <div className="bg-red-500/20 border border-red-500/50 px-4 py-3 flex items-start gap-3">
            <Lock className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="text-red-200 font-semibold">Free Messages Exhausted</p>
              <p className="text-red-100/80">You've used all 3 free messages for today. Upgrade to Pro for unlimited access.</p>
            </div>
          </div>
        )}
        
        {/* Modern Header - Responsive */}
        <header className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 bg-white/5 border-b border-white/10 flex items-center justify-between shrink-0 gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shadow-red-500/20 flex-shrink-0">
              <Bot size={18} className="text-white hidden sm:block" />
              <Bot size={18} className="text-white sm:hidden" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm sm:text-base md:text-lg font-bold tracking-tight leading-none truncate">Personal Tutor</h2>
              <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500 animate-pulse"></span>
                <span className="text-[8px] sm:text-[9px] md:text-[10px] text-gray-400 font-medium uppercase tracking-widest">Active</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button 
              onClick={() => setShowSettings(!showSettings)} 
              className="p-1.5 sm:p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-500 hover:text-blue-400" 
              title="Settings"
            >
              <Settings size={16} className="sm:hidden" />
              <Settings size={18} className="hidden sm:block" />
            </button>
            {conversation.length > 0 && selectedArticleId && (
              <button onClick={clearChat} className="p-1.5 sm:p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-500 hover:text-red-400" title="Clear Chat">
                <Trash2 size={16} className="sm:hidden" />
                <Trash2 size={18} className="hidden sm:block" />
              </button>
            )}
            <div className="w-px h-4 sm:h-6 bg-white/10 mx-0.5 sm:mx-1"></div>
            <button onClick={onClose} className="p-1.5 sm:p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-400 hover:text-white">
              <X size={18} className="sm:hidden" />
              <X size={24} className="hidden sm:block" />
            </button>
          </div>
        </header>

        {/* Settings Panel - Optimized */}
        {showSettings && (
          <div className="bg-white/5 border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4 space-y-4 max-h-[300px] overflow-y-auto">
            {/* Voice Gender */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-200 mb-2">Voice Gender</label>
              <div className="flex gap-2">
                <button
                  onClick={() => updateSettings(currentArticle?.language || 'en', 'female', selectedVoiceIndex)}
                  className={`flex-1 px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all ${
                    voiceGender === 'female'
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                  }`}
                >
                  👩 Female
                </button>
                <button
                  onClick={() => updateSettings(currentArticle?.language || 'en', 'male', selectedVoiceIndex)}
                  className={`flex-1 px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all ${
                    voiceGender === 'male'
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                  }`}
                >
                  👨 Male
                </button>
              </div>
            </div>

            {/* Text Size */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-200 mb-2">Text Size</label>
              <div className="flex gap-2">
                {(['sm', 'md', 'lg'] as const).map(size => (
                  <button
                    key={size}
                    onClick={() => updateSettings(currentArticle?.language || 'en', voiceGender, selectedVoiceIndex, size)}
                    className={`flex-1 px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all capitalize ${
                      textSize === size
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                    }`}
                  >
                    {size === 'sm' ? 'S' : size === 'md' ? 'M' : 'L'}
                  </button>
                ))}
              </div>
            </div>

            {/* Compact Mode */}
            <div className="flex items-center justify-between">
              <label className="text-xs sm:text-sm font-semibold text-gray-200">Compact Mode</label>
              <button
                onClick={() => updateSettings(currentArticle?.language || 'en', voiceGender, selectedVoiceIndex, textSize, !compactMode)}
                className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                  compactMode
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                {compactMode ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
        )}

        {/* Improved Chat Canvas */}
        <div ref={chatBodyRef} className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 lg:p-8 space-y-4 sm:space-y-5 md:space-y-6 scrollbar-thin scrollbar-thumb-white/10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]">
          {conversation.length === 0 && !isLoading && (
            <div className="h-full flex flex-col items-center justify-center text-center px-3 sm:px-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-500/10 rounded-3xl flex items-center justify-center mb-4 sm:mb-6 border border-blue-500/20 rotate-3">
  <>
                <Bot size={32} className="text-blue-400 sm:hidden" />
                <Bot size={40} className="text-blue-400 hidden sm:block" />
              </>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-rose-400 px-2">
                {currentArticle ? `Master ${currentArticle.title}` : 'Select a topic to begin'}
              </h3>
              <p className="text-gray-400 text-xs sm:text-sm md:text-base mt-2 max-w-xs mx-auto px-2">
                {currentArticle ? 'Ask me anything about this topic. I use metaphors and real examples to make learning easy.' : 'Use the selectors below the chat to choose what you want to learn about.'}
              </p>
              
              {currentArticle && (
                <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 w-full max-w-lg px-2">
                  {suggestedQuestions.map((q, i) => (
                    <button 
                      key={i} 
                      onClick={() => handleSendMessage(q)} 
                      onTouchStart={() => triggerHaptic('light')}
                      className="p-3 sm:p-4 bg-white/5 hover:bg-white/10 border border-white/5 rounded-xl sm:rounded-2xl text-left text-[11px] sm:text-xs md:text-sm text-gray-300 transition-all group hover:border-blue-500/30 active:scale-95 touch-target card-touch"
                    >
<>
                        <MessageSquare size={12} className="mb-1.5 text-blue-500 opacity-60 group-hover:opacity-100 sm:hidden" />
                        <MessageSquare size={14} className="mb-2 text-blue-500 opacity-60 group-hover:opacity-100 hidden sm:block" />
                      </>
                      <span className="line-clamp-2">{q}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {conversation.map((turn, index) => (
            <div key={index} className={`flex ${turn.role === 'user' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 px-1 sm:px-0`}>
              <div className={`flex flex-col max-w-[90%] xs:max-w-[85%] sm:max-w-[80%] md:max-w-[75%] lg:max-w-[70%] ${turn.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div className={`px-3 sm:px-4 md:px-5 py-2 sm:py-3 md:py-4 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl leading-relaxed text-xs sm:text-sm md:text-base transition-all ${
                  turn.role === 'user' 
                    ? 'bg-blue-600 text-white rounded-br-none font-medium break-words' 
                    : 'bg-white/5 text-gray-200 rounded-bl-none border border-white/10 break-words overflow-hidden'
                }`}>
                  {turn.role === 'user' ? (
                    <p className="whitespace-pre-wrap break-words">{turn.content}</p>
                  ) : (
                    <FormattedMessage content={turn.content} />
                  )}
                </div>
                <div className="flex items-center gap-2 sm:gap-3 mt-1.5 sm:mt-2 px-1">
                  <span className="text-[8px] sm:text-[9px] text-gray-500 font-bold uppercase tracking-widest">{new Date(turn.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  {turn.role === 'assistant' && isSpeechSupported && (
                    <button
                      onClick={() => handleSpeakMessage(index, turn.content)}
                      className={`p-1 rounded-md transition-all hover:bg-white/10 ${
                        playingMessageIndex === index
                          ? 'text-blue-400 bg-blue-500/10'
                          : 'text-gray-500 hover:text-blue-400'
                      }`}
                      title={playingMessageIndex === index ? 'Stop audio' : 'Play audio'}
                    >
                      {playingMessageIndex === index ? (
                        <>
                          <VolumeX size={12} className="sm:hidden" />
                          <VolumeX size={14} className="hidden sm:block" />
                        </>
                      ) : (
                        <>
                          <Volume2 size={12} className="sm:hidden" />
                          <Volume2 size={14} className="hidden sm:block" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start animate-pulse px-1 sm:px-0">
              <div className="bg-white/5 border border-white/10 px-3 sm:px-5 py-2 sm:py-4 rounded-xl sm:rounded-2xl rounded-bl-none flex items-center gap-2 sm:gap-4">
                <div className="flex gap-1 sm:gap-1.5">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-bounce"></div>
                </div>
                <span className="text-[10px] sm:text-xs font-bold text-blue-400 uppercase tracking-widest">Simplifying...</span>
              </div>
            </div>
          )}
        </div>

        {/* Command Center - Selectors Moved Here */}
        <div className="bg-[#1e293b]/50 border-t border-white/10 p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
          {/* Quick Context Switcher */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4 max-w-4xl mx-auto">
            <div className="flex flex-col gap-1 sm:gap-1.5">
              <span className="text-[8px] sm:text-[9px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-1 sm:gap-1.5 ml-1">
                <Book size={10} className="sm:hidden" />
                <Book size={11} className="hidden sm:block" />
                <span> Roadmap</span>
              </span>
              <Select 
                onChange={handleRoadmapChange} 
                value={selectedRoadmapId || ''} 
                options={[{ value: '', label: 'Select roadmap...' }, ...roadmaps.map(r => ({ value: r.id || '', label: r.title }))]}
                className="bg-[#0f172a] border-white/5 h-8 sm:h-9 text-xs sm:text-sm rounded-xl"
              />
            </div>
            <div className="flex flex-col gap-1 sm:gap-1.5">
              <span className="text-[8px] sm:text-[9px] font-bold text-gray-500 uppercase tracking-widest flex items-center gap-1 sm:gap-1.5 ml-1">
                <FileText size={10} className="sm:hidden" />
                <FileText size={11} className="hidden sm:block" />
                <span> Article/Topic</span>
              </span>
              <Select 
                onChange={(e) => setSelectedArticleId(e.target.value)} 
                value={selectedArticleId || ''} 
                disabled={!selectedRoadmapId}
                options={loadingArticles ? [{ value: '', label: 'Loading...' }] : [{ value: '', label: 'Pick a topic...' }, ...Object.values(fetchedArticles).filter((a): a is ArticleData & { id: string } => !!a.id).map(a => ({ value: a.id, label: a.title }))]}
                className="bg-[#0f172a] border-white/5 h-8 sm:h-9 text-xs sm:text-sm rounded-xl"
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            {error && <div className="mb-3 sm:mb-4"><ErrorAlert message={error} /></div>}

            {/* Attached Files Preview */}
            {attachedFiles.length > 0 && (
              <div className="mb-3 sm:mb-4 p-3 sm:p-4 md:p-5 bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl space-y-2 sm:space-y-3">
                <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5 sm:gap-2">
                  <FileUp size={10} className="sm:hidden" />
                  <FileUp size={12} className="hidden sm:block" />
                  <span>{attachedFiles.length} File{attachedFiles.length !== 1 ? 's' : ''} Attached</span>
                </p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {attachedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-white/5 border border-white/10 rounded-lg group hover:border-blue-500/30">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {file.type === 'image' ? (
                          <>
                            <Image size={12} className="text-blue-400 flex-shrink-0 sm:hidden" />
                            <Image size={14} className="text-blue-400 flex-shrink-0 hidden sm:block" />
                          </>
                        ) : (
                          <>
                            <FileText size={12} className="text-rose-400 flex-shrink-0 sm:hidden" />
                            <FileText size={14} className="text-rose-400 flex-shrink-0 hidden sm:block" />
                          </>
                        )}
                        <span className="text-[10px] sm:text-xs text-gray-300 truncate">{file.name}</span>
                        <span className="text-[8px] sm:text-[9px] text-gray-500 flex-shrink-0">({formatFileSize(file.size)})</span>
                      </div>
                      <button onClick={() => removeAttachedFile(idx)} className="p-0.5 sm:p-1 hover:bg-white/10 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={10} className="text-gray-400 sm:hidden" />
                        <X size={12} className="text-gray-400 hidden sm:block" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className={`relative flex items-end gap-2 sm:gap-3 group transition-all ${isDraggingFiles ? 'bg-blue-500/10 border-blue-500/30 rounded-2xl p-2' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="relative flex-1">
                {/* File upload button - LEFT SIDE */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || !currentArticle}
                  className="absolute left-2 sm:left-3 bottom-2 sm:bottom-3 md:bottom-4 z-10 w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-red-600 hover:bg-red-500 disabled:bg-white/5 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded-lg sm:rounded-xl flex items-center justify-center transition-all active:scale-95 shadow-lg shadow-red-500/20 hover:shadow-red-500/40"
                  title="Attach files (images, PDFs, docs) - Shift+Click or drag files"
                >
                  <Paperclip size={14} className="sm:hidden" />
                  <Paperclip size={16} className="hidden sm:block md:hidden" />
                  <Paperclip size={18} className="hidden md:block" />
                </button>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />

                <textarea
                  ref={textareaRef}
                  value={userInput}
                  onChange={(e) => {
                    setUserInput(e.target.value);
                    // Auto-resize textarea based on content
                    if (textareaRef.current) {
                      textareaRef.current.style.height = 'auto';
                      const newHeight = Math.min(Math.max(textareaRef.current.scrollHeight, 44), 200);
                      textareaRef.current.style.height = `${newHeight}px`;
                    }
                  }}
                  onKeyDown={(e) => { 
                    if (e.key === 'Enter' && !e.shiftKey) { 
                      e.preventDefault(); 
                      if (userInput.trim() && !isLoading) handleSendMessage(); 
                    } 
                  }}
                  placeholder={currentArticle ? "Ask anything about this topic... 💡 (Shift+Enter for new line)" : "👆 Select a topic first to start learning"}
                  className={`w-full bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-2 border-white/15 text-white/95 rounded-lg sm:rounded-2xl px-3 sm:px-4 md:px-5 py-2 sm:py-3 md:py-4 pl-10 sm:pl-12 md:pl-16 pr-12 sm:pr-14 md:pr-16 resize-none focus:ring-0 focus:border-blue-500/60 focus:bg-gradient-to-b focus:from-slate-800/70 focus:to-slate-900/70 outline-none transition-all duration-200 placeholder:text-gray-500/70 shadow-lg shadow-black/30 leading-6 ${
                    textSize === 'sm' ? 'text-xs sm:text-xs' : textSize === 'md' ? 'text-xs sm:text-sm md:text-base' : 'text-sm sm:text-base md:text-lg'
                  }`}
                  rows={1}
                  disabled={!currentArticle || isLoading}
                  spellCheck="true"
                  style={{ 
                    minHeight: '44px',
                    maxHeight: '200px',
                    overflow: 'hidden'
                  }}
                />
                
                {/* Character count and status indicator */}
                {userInput && (
                  <div className="absolute right-12 sm:right-14 md:right-16 bottom-2 sm:bottom-3 md:bottom-4 text-[9px] sm:text-xs text-gray-500/60 font-medium">
                    {userInput.length}
                  </div>
                )}
                
                {/* Send button - RIGHT SIDE */}
                <button
                  onClick={() => handleSendMessage()}
                  onTouchStart={() => triggerHaptic('light')}
                  disabled={isLoading || !userInput.trim() || !currentArticle || (!isPro && user && !canSendMessage) || false}
                  className={`absolute right-2 sm:right-3 bottom-2 sm:bottom-3 md:bottom-4 w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-200 active:scale-95 touch-target btn-touch shadow-lg font-semibold ${
                    isLoading || !userInput.trim() || !currentArticle || (!isPro && user && !canSendMessage)
                      ? 'bg-white/5 text-gray-600 cursor-not-allowed'
                      : 'bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white hover:shadow-blue-500/40 shadow-blue-600/30'
                  }`}
                  title={!isPro && user && !canSendMessage ? "Free messages limit reached. Upgrade to Pro." : userInput.trim() ? "Send message (Enter)" : "Type something to send"}
                >
                  {isLoading ? (
                    <>
                      <Loader className="animate-spin sm:hidden" size={14} />
                      <Loader className="animate-spin hidden sm:block md:hidden" size={16} />
                      <Loader className="animate-spin hidden md:block" size={18} />
                    </>
                  ) : userInput.trim() ? (
                    <>
                      <Send size={14} className="transition-transform hover:scale-110 sm:hidden" />
                      <Send size={16} className="transition-transform hover:scale-110 hidden sm:block md:hidden" />
                      <Send size={18} className="transition-transform hover:scale-110 hidden md:block" />
                    </>
                  ) : (
                    <>
                      <Send size={14} className="sm:hidden" />
                      <Send size={16} className="hidden sm:block md:hidden" />
                      <Send size={18} className="hidden md:block" />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Input hints */}
            <div className="mt-2 sm:mt-3 px-2 sm:px-4 md:px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-2 sm:gap-3 md:gap-6">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[9px] sm:text-[11px] font-semibold text-gray-500/70 uppercase tracking-wider">
                <span className="flex items-center gap-1 sm:gap-1.5 hover:text-gray-400 transition-colors">
                  <CornerDownLeft size={11} className="text-blue-500/50 sm:hidden" />
                  <CornerDownLeft size={13} className="text-blue-500/50 hidden sm:block" />
                  <span> Enter to send</span>
                </span>
                <span className="hidden md:flex items-center gap-1.5 hover:text-gray-400 transition-colors">
                  <ChevronUp size={13} className="text-blue-500/50" /> Shift+Enter for new line
                </span>
              </div>
              <span className="hidden md:flex items-center gap-1.5 text-blue-400/60 text-[11px] font-semibold">
                <Sparkles size={13} className="text-blue-500/70" /> Powered by Gemini 2.0
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextualTutorModal;
